import React, { useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import { Currency, DataGrid, Header2Text, Notification } from 'ui-toolkit';
import { ITableChangeState } from 'react-bootstrap-table-next';
import styled from 'styled-components';
import { IEscrowJournalEntryQuery } from '../../../../types/accounting/query/IEscrowJournalEntryQuery';
import {
  prettyJournalEntryPaymentMethod,
  prettyJournalEntryPaymentMethodType,
} from '../../../../types/accounting/eJournalEntryPaymentMethod';
import { IEscrowJournalEntryData } from '../../../../types/accounting/data/IEscrowJournalEntryData';
import { useAsyncOperationWithData } from '../../../../utilities/accounting/hooks/useAsyncOperationWithData';
import { getJournalEntriesAsync } from '../../../../API/accounting/getJournalEntriesAsync';
import { useHasPermissions } from '../../../accessControl/hooks/useHasPermissions';
import { ePermissions } from 'types/administration/userAdministration/ePermissions';

export interface IEscrowFileJournalEntriesContainerProps {
  escrowFileId: string;
}

const GridWrapper = styled.div`
  overflow: scroll;
`;

export const EscrowFileJournalEntriesContainer: React.FC<IEscrowFileJournalEntriesContainerProps> = ({
  escrowFileId,
}) => {
  const hasReadPermission = useHasPermissions([ePermissions.EscrowJournalEntriesRead]);
  const [query, setQuery] = useState<IEscrowJournalEntryQuery>({
    escrowFileId,
    pageNumber: 1,
    pageSize: 10,
    sortField: 'initiatedAtDate',
    sortOrder: 'desc',
  });

  const [{ data, fetchStatus }, loadJournalEntries] = useAsyncOperationWithData(getJournalEntriesAsync);

  useEffect(() => {
    if (hasReadPermission) loadJournalEntries(query);
  }, [hasReadPermission, loadJournalEntries, query]);

  return hasReadPermission ? (
    <>
      <Header2Text>Journal Entries</Header2Text>
      <GridWrapper>
        {!!data && (
          <DataGrid
            columns={[
              {
                dataField: 'createdAt',
                formatter: (column: any) => DateTime.fromISO(column as string).toFormat('D'),
                headerStyle: { width: 120 },
                text: 'Created At',
              },
              {
                dataField: 'paymentMethod',
                formatter: (paymentMethod, row) => prettyJournalEntryPaymentMethod[row!.paymentMethod],
                headerStyle: { width: 130 },
                text: 'Method',
              },
              {
                dataField: 'note',
                headerStyle: { width: 'auto' },
                text: 'Note',
              },
              {
                dataField: 'escrowFile.order.fileNumber',
                headerStyle: { width: 120 },
                text: 'File Number',
              },
              {
                dataField: 'amountCents',
                formatter: (value) => typeof value === 'number' && <Currency value={value} valueIsCents={true} />,
                headerStyle: { width: 120 },
                text: 'Amount',
              },
              {
                dataField: 'entryType',
                formatter: (entryType, row) => prettyJournalEntryPaymentMethodType[row!.entryType],
                headerStyle: { width: 120 },
                text: 'Entry Type',
              },
              {
                dataField: 'initiatedAtDate',
                formatter: (column: any) => column && DateTime.fromISO(column as string).toFormat('D'),
                headerStyle: { width: 130 },
                text: 'Initiated At',
              },
              {
                dataField: 'completedAtDate',
                formatter: (column: any) => column && DateTime.fromISO(column as string).toFormat('D'),
                headerStyle: { width: 130 },
                text: 'Completed At',
              },
            ]}
            data={data.content}
            keyField="id"
            loading={fetchStatus.fetching}
            onTableChange={(type: string, tableState: ITableChangeState<IEscrowJournalEntryData>) => {
              setQuery({
                ...query,
                pageNumber: tableState.page,
              });
            }}
            remote={true}
            pagination={{
              page: query.pageNumber || 1,
              sizePerPage: query.pageSize || 10,
              totalSize: data.totalElements,
            }}
            sort={{ dataField: query.sortField || 'UNSET', order: query.sortOrder || 'asc' }}
          />
        )}
      </GridWrapper>
    </>
  ) : (
    <Notification type="error" message="You do not have permission to see Journal Entries." />
  );
};
